import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

export const useCustomerPortalHashMap = () => {
  const { hash, state } = useLocation();
  const router = useHistory();
  const hashMap = useMemo(() => {
    const split = hash.replace('#', '').split('&');

    return split.reduce((acc, curr) => {
      const [key, value] = curr.split('=');

      if (!key) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {} as Record<string, string>);
  }, [hash]);

  const constructHash = useCallback(
    (entries?: Record<string, unknown>) => {
      return Object.entries(entries ?? hashMap)
        .filter(([key]) => !!key)

        .map(([key, value]) => `${key}=${value}`)
        .join('&');
    },
    [hashMap],
  );

  const setHashMap = useCallback(
    (_key: string, _value: string) => {
      hashMap[_key] = _value;

      router.replace({
        hash: constructHash(),
        state,
      });
    },
    [constructHash, hashMap, router, state],
  );

  const pushToHashMap = useCallback(
    (_key: string, _value: string) => {
      hashMap[_key] = _value;

      router.push({
        hash: constructHash(),
        state,
      });
    },
    [constructHash, hashMap, router, state],
  );

  const removeKey = useCallback(
    (_key: string) => {
      delete hashMap[_key];

      router.replace({
        hash: constructHash(),
        state,
      });
    },
    [constructHash, hashMap, router, state],
  );

  const overwriteHashMap = useCallback(
    (newHashMap: Record<string, string>) => {
      router.replace({
        hash: constructHash(newHashMap),
        state,
      });
    },
    [constructHash, router, state],
  );

  return { hashMap, setHashMap, removeKey, pushToHashMap, overwriteHashMap };
};
